"use strict";
// Text-decoration-line property parser
// https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-line
Object.defineProperty(exports, "__esModule", { value: true });
exports.parse = parse;
exports.toCSSValue = toCSSValue;
const shared_utils_1 = require('../utils/shared-utils.cjs');
const css_variable_1 = require('./css-variable.cjs');
const types_1 = require('../types.cjs');
// Type guard for text-decoration-line values
function isTextDecorationLine(value) {
    return types_1.TEXT_DECORATION_LINE_KEYWORDS.includes(value.toLowerCase());
}
/**
 * Parses CSS text-decoration-line property values
 *
 * Syntax: none | [ underline || overline || line-through || blink ] | global-keywords
 *
 * @param value - The CSS text-decoration-line value to parse
 * @returns Parsed TextDecorationLineValue or null if invalid
 *
 * @example
 * ```typescript
 * parse('underline') // { type: 'keyword', lines: ['underline'] }
 * parse('underline overline') // { type: 'keyword', lines: ['underline', 'overline'] }
 * parse('none') // { type: 'keyword', lines: ['none'] }
 * parse('inherit') // { type: 'keyword', keyword: 'inherit' }
 * parse('var(--decoration-line)') // { type: 'variable', CSSvariable: 'decoration-line' }
 * ```
 */
function parse(value) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }
    // CSS variables - parse and return directly
    if ((0, shared_utils_1.isCssVariable)(trimmed)) {
        return (0, css_variable_1.parse)(trimmed);
    }
    // Handle global keywords
    if ((0, shared_utils_1.isGlobalKeyword)(trimmed)) {
        return {
            type: 'keyword',
            keyword: trimmed.toLowerCase()
        };
    }
    // Parse line decoration values using shared tokenizer
    const tokens = (0, shared_utils_1.tokenize)(trimmed);
    const lineValues = [];
    // Check if 'none' is mixed with other values (invalid)
    const hasNone = tokens.some(token => token.toLowerCase() === 'none');
    const hasOthers = tokens.some(token => isTextDecorationLine(token) && token.toLowerCase() !== 'none');
    if (hasNone && hasOthers) {
        return null; // 'none' cannot be combined with other lines
    }
    // Parse each token
    for (const token of tokens) {
        if (isTextDecorationLine(token)) {
            const lineValue = token.toLowerCase();
            // Don't add duplicates
            if (!lineValues.includes(lineValue)) {
                lineValues.push(lineValue);
            }
        }
        else {
            // Invalid token
            return null;
        }
    }
    // Must have at least one line decoration specified
    if (lineValues.length === 0) {
        return null;
    }
    return {
        type: 'keyword',
        lines: lineValues
    };
}
/**
 * Converts a parsed TextDecorationLineValue back to its CSS string representation
 *
 * @param value - The parsed text-decoration-line value
 * @returns CSS string representation or null if invalid
 */
function toCSSValue(value) {
    if (!value) {
        return null;
    }
    // Handle CSS variables
    if (value.type === 'variable') {
        return (0, css_variable_1.toCSSValue)(value);
    }
    // Handle keyword values
    if (value.type === 'keyword') {
        // Handle global keywords
        if ('keyword' in value) {
            return value.keyword;
        }
        // Handle line keywords
        if ('lines' in value) {
            const lines = value.lines;
            if (lines.length === 0) {
                return null;
            }
            return lines.join(' ');
        }
    }
    return null;
}
